import { HttpClient } from '@shared/adapters/HttpClient';
import type { Order } from '../models/Order';
import { adaptOrder } from './Order.adapter';
import { UpdateOrderPayload } from './types';

export interface PatchShippedPaymentPayload {
  total?: number;
  payment_method_id?: number;
  reason: string;
}

export interface PaymentCorrection {
  id: number;
  order_id: number;
  user_id: number;
  reason: string;
  adjustment_type: string;
  adjustment_amount: number;
  before_snapshot: {
    total: number;
    payment_method_id: number;
    total_shipping: number;
    total_discount: number;
    total_tax: number;
  };
  after_snapshot: {
    total: number;
    payment_method_id: number;
    total_shipping: number;
    total_discount: number;
    total_tax: number;
  };
  created_at: string;
  updated_at: string;
}

export interface CreateShipmentStatusCorrectionPayload {
  shipment_status_id: number;
  reason: string;
  attachments?: File[];
}

export interface ShipmentStatusCorrectionSnapshot {
  shipment_status_mapped_id: number | null;
  shipment_status_id: number;
  shipment_status_name: string;
  date_tracking_update: string | null;
}

export interface ShipmentStatusCorrection {
  id: number;
  order_id: number;
  user_id?: number;
  user_name?: string;
  reason: string;
  before_snapshot: ShipmentStatusCorrectionSnapshot;
  after_snapshot: ShipmentStatusCorrectionSnapshot;
  attachments: string[];
  created_at: string;
  updated_at: string;
}

export class OrdersApiAdapter {
  constructor(private httpClient: HttpClient) {}

  async getOrderById(orderId: number): Promise<Order> {
    const response = await this.httpClient.get<any>(`/orders/${orderId}`);
    return adaptOrder(response);
  }

  async updateOrder(orderId: number, payload: UpdateOrderPayload): Promise<Order> {
    const response = await this.httpClient.put<any>(`/orders/${orderId}`, payload);
    return adaptOrder(response);
  }

  async updateFromShop(orderId: number): Promise<void> {
    await this.httpClient.put(`/orders/${orderId}/update-from-shop`, {});
  }

  async patchShippedPaymentDetails(orderId: number, payload: PatchShippedPaymentPayload): Promise<any> {
    return this.httpClient.patch<any>(`/orders/${orderId}/shipped-payment-details`, payload);
  }

  async getPaymentCorrections(orderId: number): Promise<{ data: PaymentCorrection[] }> {
    return this.httpClient.get<{ data: PaymentCorrection[] }>(`/orders/${orderId}/payment-corrections`);
  }

  async createShipmentStatusCorrection(orderId: number, payload: CreateShipmentStatusCorrectionPayload): Promise<any> {
    const formData = new FormData();
    formData.append('shipment_status_id', String(payload.shipment_status_id));
    formData.append('reason', payload.reason);
    payload.attachments?.forEach((file) => {
      formData.append('attachments[]', file, file.name);
    });
    return this.httpClient.postMultipart(`/orders/${orderId}/shipment-status`, formData);
  }

  async getShipmentStatusCorrections(orderId: number): Promise<{ data: ShipmentStatusCorrection[] }> {
    return this.httpClient.get<{ data: ShipmentStatusCorrection[] }>(`/orders/${orderId}/shipment-status-corrections`);
  }
}
